<script lang="ts">
	import '../app.css';
	import favicon from '$lib/assets/favicon.svg';
	import { Button, buttonVariants } from '$lib/components/ui/button/index.js';
	import * as Card from '$lib/components/ui/card/index.js';
	import * as Dialog from '$lib/components/ui/dialog/index.js';
	import * as Tooltip from '$lib/components/ui/tooltip/index.js';
	import { page } from '$app/stores';
	import { authStatus, authStatusError, refreshAuthStatus } from '$lib/stores/auth';
	import { isAnonymousReview } from '$lib/config/review';
	import { mujocoInstance, scenes, selectedScene } from '$lib/stores/mujoco';
	import {
		initializeIkRuntime,
		initializeG1Solver,
		isIkLoading,
		isIkReady,
		ikError,
		isG1SolverReady
	} from '$lib/stores/ik';
	import {
		debugEnabled,
		debugBodyTracking,
		debugIk,
		showInfoCard,
		setDebugMode,
		setDebugCategory,
		updateUIConfig
	} from '$lib/stores/config';
	import { downloadExampleScenes } from '$lib/mujoco/loading';
	import { loadSceneWithAssets } from '$lib/services/mujoco-asset-loader';
	import { onMount } from 'svelte';
	let { children } = $props();

	let mujoco = $state<import('mujoco_wasm_contrib/debug').mujoco | null>(null);
	mujocoInstance.subscribe((value) => {
		mujoco = value;
	});

	let filesystemData = $state<any[]>([]);
	let selectedFile = $state<string | null>(null);
	let fileContent = $state<string>('');
	let debugDialogOpen = $state(false);

	$effect(() => {
		if (debugDialogOpen) {
			refreshAuthStatus();
		}
	});

	function readDirectory(path: string = '/working'): any[] {
		if (!mujoco) return [];

		try {
			const files = mujoco.FS.readdir(path);
			return files
				.filter((f: string) => f !== '.' && f !== '..')
				.map((filename: string) => {
					const fullPath = path === '/' ? `/${filename}` : `${path}/${filename}`;
					try {
						const stat = mujoco!.FS.stat(fullPath);
						return {
							name: filename,
							path: fullPath,
							isDirectory: mujoco!.FS.isDir(stat.mode),
							size: stat.size,
							type: mujoco!.FS.isDir(stat.mode) ? 'directory' : 'file'
						};
					} catch (e: any) {
						return {
							name: filename,
							path: fullPath,
							isDirectory: false,
							size: 0,
							type: 'unknown',
							error: e.message
						};
					}
				});
		} catch (e: any) {
			console.error('Failed to read directory:', path, e);
			return [];
		}
	}

	function refreshFilesystem() {
		if (!mujoco) return;

		function buildTree(path: string): any[] {
			const items = readDirectory(path);
			return items.map((item) => {
				if (item.isDirectory) {
					return {
						...item,
						children: buildTree(item.path)
					};
				}
				return item;
			});
		}

		filesystemData = buildTree('/working');
	}

	function viewFile(filepath: string) {
		if (!mujoco) return;

		try {
			selectedFile = filepath;
			const data = mujoco.FS.readFile(filepath);

			if (filepath.endsWith('.xml') || filepath.endsWith('.txt') || filepath.endsWith('.obj')) {
				fileContent = new TextDecoder().decode(data);
			} else if (filepath.endsWith('.png') || filepath.endsWith('.jpg')) {
				fileContent = `[Binary Image File - ${data.length} bytes]\nUse download to save the file.`;
			} else {
				fileContent = `[Binary File - ${data.length} bytes]\nUse download to save the file.`;
			}
		} catch (e: any) {
			fileContent = `Error reading file: ${e.message}`;
		}
	}

	function downloadFile(filepath: string, filename: string) {
		if (!mujoco) return;

		try {
			const data = mujoco.FS.readFile(filepath);
			const blob = new Blob([data as unknown as BlobPart]);
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = filename;
			a.click();
			URL.revokeObjectURL(url);
		} catch (e: any) {
			alert(`Error downloading file: ${e.message}`);
		}
	}

	onMount(() => {
		const defaultScenes = {
			// Humanoid: 'humanoid.xml',
			Cassie: 'agility_cassie/scene.xml'
			// Hammock: 'hammock.xml',
			// Balloons: 'balloons.xml',
			// Hand: 'shadow_hand/scene_right.xml',
			// Flag: 'flag.xml',
			// Mug: 'mug.xml',
			// Tendon: 'model_with_tendon.xml'
		};
		scenes.set(defaultScenes);
		// selectedScene.set(defaultScenes.Humanoid);

		// Initialize SEW-Mimic wasm runtime in background (non-blocking)
		initializeIkRuntime()
			.then(() => {
				console.log('✅ SEW-Mimic runtime initialized');
			})
			.catch((error) => {
				console.error('❌ Failed to initialize SEW-Mimic runtime:', error);
			});

		refreshAuthStatus();

		const handleFocus = () => refreshAuthStatus();
		window.addEventListener('focus', handleFocus);
		return () => {
			window.removeEventListener('focus', handleFocus);
		};
	});

	async function onSceneSelect(event: Event) {
		const target = event.target as HTMLSelectElement;
		const scenePath = target.value;
		if (mujoco) {
			selectedScene.set(scenePath);
		}
	}

	async function onDownloadExampleScenes() {
		if (mujoco) {
			await downloadExampleScenes(mujoco);
			alert('Example scenes downloaded and mounted.'); // TODO: replace with shadcn-svelte's `Alert`
		}
	}

	async function onDownloadRobots() {
		if (mujoco) {
			await loadSceneWithAssets(mujoco, 'models/humanoid/humanoid.xml'); // DEBUG
			// await loadSceneWithAssets(mujoco, 'models/agility_cassie/scene.xml'); // DEBUG
			// await loadSceneWithAssets(mujoco, 'models/boston_dynamics_spot/scene.xml'); // DEBUG
			// await loadSceneWithAssets(mujoco, 'models/rby1a/mujoco/model.xml');
			await loadSceneWithAssets(mujoco, 'models/unitree_g1/g1.xml');
			// await loadSceneWithAssets(mujoco, 'models/unitree_g1/scene.xml');

			// await loadSceneWithAssets(mujoco, 'models/unitree_a1/scene.xml');
			// await loadSceneWithAssets(mujoco, 'models/unitree_go2/scene.xml');
			// await loadSceneWithAssets(mujoco, 'models/unitree_h1/scene.xml');
			// await loadSceneWithAssets(mujoco, 'models/unitree_z1/scene.xml');
			alert('Robots downloaded and mounted.'); // TODO: replace with shadcn-svelte's `Alert`
		}
	}

	async function onInitializeG1Solver() {
		try {
			await initializeG1Solver({
				debug: false
			});
			alert('G1 IK solver initialized successfully!');
		} catch (error) {
			alert(`Failed to initialize G1 IK solver: ${error}`);
		}
	}
</script>

<svelte:head>
	<link rel="icon" href={favicon} />
	<title>SEW Geometric Teleop</title>
</svelte:head>

<header class="flex h-14 items-center px-4 lg:px-6">
	<div class="flex flex-1 items-center">
		<a class="flex items-center justify-center" href="/">
			<span class="text-md font-semibold transition-all hover:font-bold">SEW Geometric Teleop</span>
		</a>
	</div>
	<nav class="flex flex-none flex-wrap items-center justify-center gap-4 sm:gap-6">
		<!-- <Button variant="ghost" href="/" class="text-sm font-medium">Project</Button> -->
		<Button
			variant="ghost"
			href="/demo"
			class={$page.url.pathname === '/demo' ? 'bg-stone-200 dark:bg-stone-800' : ''}
			>Live Demo</Button
		>
		<Button
			variant="ghost"
			href="/"
			class={$page.url.pathname === '/' ? 'bg-stone-200 dark:bg-stone-800' : ''}>Videos</Button
		>
		<!-- <Button variant="ghost" href="/details" class="text-sm font-medium">Details</Button> -->
		<!-- <Button variant="ghost" href="/people" class="text-sm font-medium">People</Button> -->
		<Button
			variant="ghost"
			href="/benchmarks"
			class={$page.url.pathname === '/benchmarks' ? 'bg-stone-200 dark:bg-stone-800' : ''}
			>Benchmarks</Button
		>
		<!--
                <Button
                        variant="link"
                        href="https://TODO.com"
                        class="text-sm font-medium transition-all hover:text-red-700 dark:hover:text-red-400"
                        >arXiv</Button
                >
                <Button
                        variant="link"
                        href="https://TODO.com"
                        class="text-sm font-medium transition-all hover:text-fuchsia-700 dark:hover:text-fuchsia-400"
                        >GitHub</Button
                >
                -->
		{#if !isAnonymousReview}
			<Button
				variant="ghost"
				href="/authors"
				class={$page.url.pathname === '/authors' ? 'bg-stone-200 dark:bg-stone-800' : ''}
				>Authors</Button
			>
			<Button
				variant="link"
				href="https://arxiv.org/abs/2602.01632"
				class="text-sm font-medium transition-all hover:text-red-700 dark:hover:text-red-400"
				target="_blank"
			>
				arXiv
			</Button>
		{/if}
	</nav>
	<div class="flex flex-1 justify-end"></div>
</header>

{@render children?.()}

<footer
	class="flex w-full shrink-0 flex-col items-center gap-2 border-t px-4 py-6 sm:flex-row md:px-6"
>
	<!-- <p class="text-xs text-gray-500 dark:text-gray-400">© 2025 Safe Robotics Lab at Georgia Tech. All rights reserved.</p> -->
	{#if isAnonymousReview}
		<p class="text-xs text-gray-500 dark:text-gray-400">© 2025. All rights reserved.</p>
	{:else}
		<p class="text-xs text-gray-500 dark:text-gray-400">
			© 2025
			<Button
				variant="link"
				href="https://saferoboticslab.me.gatech.edu/"
				class="h-auto p-0 text-xs font-normal text-gray-500 dark:text-gray-400"
				target="_blank"
			>
				Safe Robotics Lab at Georgia Tech.
			</Button>
			All rights reserved.
		</p>
	{/if}
	<nav class="flex gap-4 sm:ml-auto sm:gap-6">
		<Dialog.Root bind:open={debugDialogOpen}>
			<Dialog.Trigger class="link text-xs opacity-20">Debug</Dialog.Trigger>
			<Dialog.Content class="max-h-[80vh] overflow-y-auto sm:max-w-[80vw]">
				<Dialog.Header>
					<Dialog.Title>Debug</Dialog.Title>
					<!-- <Dialog.Description>Debug information will be displayed here.</Dialog.Description> -->
				</Dialog.Header>
				<div class="grid gap-4 py-4 md:grid-cols-2 lg:grid-cols-3">
					<div class="md:col-span-2 lg:col-span-3">
						<div class="grid gap-4 md:grid-cols-3">
							<Button onclick={onDownloadExampleScenes} disabled={!mujoco}>
								Download Example Scenes
							</Button>
							<Button onclick={onDownloadRobots} disabled={!mujoco}>Download Main Robots</Button>
							<Button onclick={refreshFilesystem} disabled={!mujoco}>Refresh Filesystem</Button>
						</div>
					</div>
					<div>
						<Card.Root>
							<Card.Header>
								<Card.Title>Debug Configuration</Card.Title>
							</Card.Header>
							<Card.Content>
								<div class="space-y-3 text-sm">
									<div class="flex items-center justify-between">
										<span>Global Debug Mode:</span>
										<label class="flex items-center">
											<input
												type="checkbox"
												bind:checked={$debugEnabled}
												onchange={(e) => setDebugMode((e.target as HTMLInputElement).checked)}
												class="mr-2"
											/>
											{$debugEnabled ? 'Enabled' : 'Disabled'}
										</label>
									</div>

									<div class="flex items-center justify-between">
										<span>Body Tracking Debug:</span>
										<label class="flex items-center">
											<input
												type="checkbox"
												bind:checked={$debugBodyTracking}
												onchange={(e) =>
													setDebugCategory('bodyTracking', (e.target as HTMLInputElement).checked)}
												disabled={!$debugEnabled}
												class="mr-2"
											/>
											{$debugBodyTracking ? 'On' : 'Off'}
										</label>
									</div>

									<div class="flex items-center justify-between">
										<span>IK Debug:</span>
										<label class="flex items-center">
											<input
												type="checkbox"
												bind:checked={$debugIk}
												onchange={(e) =>
													setDebugCategory('ik', (e.target as HTMLInputElement).checked)}
												disabled={!$debugEnabled}
												class="mr-2"
											/>
											{$debugIk ? 'On' : 'Off'}
										</label>
									</div>

									<div class="flex items-center justify-between">
										<span>Show Info Cards:</span>
										<label class="flex items-center">
											<input
												type="checkbox"
												bind:checked={$showInfoCard}
												onchange={(e) =>
													updateUIConfig({
														show_info_card: (e.target as HTMLInputElement).checked
													})}
												class="mr-2"
											/>
											{$showInfoCard ? 'On' : 'Off'}
										</label>
									</div>
									<div class="flex items-center justify-between">
										<span>Logged In:</span>
										<span
											class={$authStatus === null
												? 'text-gray-500'
												: $authStatus.loggedIn
													? 'text-green-600'
													: 'text-red-600'}
										>
											{$authStatus === null ? 'Unknown' : $authStatus.loggedIn ? 'Yes' : 'No'}
										</span>
									</div>
								</div>
							</Card.Content>
						</Card.Root>
					</div>
					<div>
						<Card.Root>
							<Card.Header>
								<Card.Title>Scenes</Card.Title>
							</Card.Header>
							<Card.Content>
								{#if $scenes}
									<pre>{JSON.stringify($scenes, null, 2)}</pre>
								{/if}
							</Card.Content>
						</Card.Root>
					</div>
					<div>
						<Card.Root>
							<Card.Header>
								<Card.Title>Debug Headers</Card.Title>
							</Card.Header>
							<Card.Content>
								<div class="mb-2 flex items-center justify-between gap-2">
									<span class="text-xs text-gray-500">Live request headers.</span>
									<Button size="sm" variant="outline" onclick={() => refreshAuthStatus()}
										>Refresh</Button
									>
								</div>
								{#if $authStatusError}
									<div class="text-sm text-red-600">{$authStatusError}</div>
								{:else if $authStatus?.debugHeaders}
									<pre class="max-h-64 overflow-y-auto rounded border p-2 text-xs">{JSON.stringify(
											$authStatus.debugHeaders,
											null,
											2
										)}</pre>
								{:else if $authStatus === null}
									<div class="text-sm text-gray-500">Loading header data…</div>
								{:else}
									<div class="text-sm text-gray-500">No header data available.</div>
								{/if}
							</Card.Content>
						</Card.Root>
					</div>
					<div>
						<Card.Root>
							<Card.Header>
								<Card.Title>Select Scene</Card.Title>
							</Card.Header>
							<Card.Content>
								{#if $scenes && $selectedScene}
									<select class="w-full p-2" onchange={onSceneSelect}>
										{#each Object.entries($scenes) as [name, path]}
											<option value={path} selected={$selectedScene === path}>{name}</option>
										{/each}
									</select>
								{/if}
							</Card.Content>
						</Card.Root>
					</div>
					<div class="md:col-span-2 lg:col-span-2 lg:col-start-1">
						<Card.Root>
							<Card.Header>
								<Card.Title>MEMFS Filesystem Browser</Card.Title>
							</Card.Header>
							<Card.Content>
								{#if filesystemData.length > 0}
									<div class="grid grid-cols-1 gap-4 md:grid-cols-2">
										<div class="space-y-2">
											<h4 class="font-medium">Files & Directories:</h4>
											<div class="max-h-64 overflow-y-auto rounded border p-2 text-sm">
												{#each filesystemData as item}
													<div class="space-y-1">
														{#if item.isDirectory}
															<div class="font-medium text-blue-600">📁 {item.name}/</div>
															{#if item.children}
																{#each item.children as child}
																	<div class="ml-4 flex items-center justify-between">
																		<button
																			type="button"
																			class="cursor-pointer rounded px-1 text-left hover:bg-gray-100"
																			onclick={() => viewFile(child.path)}
																		>
																			{child.isDirectory ? '📁' : '📄'}
																			{child.name}
																		</button>
																		<div class="flex gap-1">
																			<span class="text-xs text-gray-500">{child.size}b</span>
																			{#if !child.isDirectory}
																				<button
																					class="text-xs text-blue-600 hover:underline"
																					onclick={() => downloadFile(child.path, child.name)}
																					>↓</button
																				>
																			{/if}
																		</div>
																	</div>
																{/each}
															{/if}
														{:else}
															<div class="flex items-center justify-between">
																<button
																	type="button"
																	class="cursor-pointer rounded px-1 text-left hover:bg-gray-100"
																	onclick={() => viewFile(item.path)}
																>
																	📄 {item.name}
																</button>
																<div class="flex gap-1">
																	<span class="text-xs text-gray-500">{item.size}b</span>
																	<button
																		class="text-xs text-blue-600 hover:underline"
																		onclick={() => downloadFile(item.path, item.name)}>↓</button
																	>
																</div>
															</div>
														{/if}
													</div>
												{/each}
											</div>
										</div>
										<div class="space-y-2">
											<h4 class="font-medium">File Viewer:</h4>
											{#if selectedFile}
												<div class="text-xs text-gray-600">Viewing: {selectedFile}</div>
												<textarea
													readonly
													class="h-64 w-full resize-none rounded border p-2 font-mono text-xs"
													value={fileContent}
												></textarea>
											{:else}
												<div
													class="flex h-64 items-center justify-center rounded border p-2 text-sm text-gray-500"
												>
													Click on a file to view its contents
												</div>
											{/if}
										</div>
									</div>
								{:else}
									<div class="p-4 text-center text-sm text-gray-500">
										No files found. Click "Refresh Filesystem" to scan MEMFS.
									</div>
								{/if}
							</Card.Content>
						</Card.Root>
					</div>
					<div>
						<Card.Root>
							<Card.Header>
								<Card.Title>IK Runtime Status</Card.Title>
							</Card.Header>
							<Card.Content>
								<div class="space-y-2 text-sm">
									<div class="flex items-center gap-2">
										<span>WASM Runtime:</span>
										{#if $isIkLoading}
											<span class="text-yellow-600">⏳ Loading...</span>
										{:else if $isIkReady}
											<span class="text-green-600">✅ Ready</span>
										{:else if $ikError}
											<span class="text-red-600">❌ Error: {$ikError}</span>
										{:else}
											<span class="text-gray-600">⭕ Not initialized</span>
										{/if}
									</div>
									<div class="flex items-center gap-2">
										<span>G1 IK Solver:</span>
										{#if $isG1SolverReady}
											<span class="text-green-600">✅ Ready</span>
										{:else}
											<span class="text-gray-600">⭕ Not initialized</span>
										{/if}
									</div>
								</div>
								<div class="mt-4">
									<Button onclick={onInitializeG1Solver} disabled={!$isIkReady}>
										Initialize G1 IK Solver
									</Button>
								</div>
							</Card.Content>
						</Card.Root>
					</div>
				</div>
				<Dialog.Footer>
					<Dialog.Close>
						<Button type="submit">Close</Button>
					</Dialog.Close>
				</Dialog.Footer>
			</Dialog.Content>
		</Dialog.Root>
		{#if !isAnonymousReview}
			<Button variant="link" href="https://github.com/safe-robotics-lab-gt" class="text-xs">
				Safe Robotics Lab @ GitHub
			</Button>
		{/if}
		<!-- <Button variant="link" href="#" class="text-xs">
			Privacy
		</Button> -->
	</nav>
</footer>
